import { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

export type ModalType = 'createTask' | 'createTaskList' | 'editTaskList' | 'createEvent' | 'createPerson' | 'createContact' | 'editTask' | 'editEvent' | 'editPerson' | 'editContact' | 'viewPerson' | 'viewTask' | 'viewEvent' | 'create_document' | 'create_note' | 'edit_document' | 'account_settings' | 'profile_edit' | 'workspace_settings' | 'upgrade';

interface ModalState {
  type: ModalType | null;
  data?: any;
}

export const useModalNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [modalState, setModalState] = useState<ModalState>({ type: null });

  // Check if modal should be open based on URL params
  const getModalFromParams = useCallback((): ModalState => {
    if (searchParams.get('create') === 'task') {
      return { 
        type: 'createTask', 
        data: { listId: searchParams.get('listId') } 
      };
    }
    if (searchParams.get('create') === 'tasklist') {
      return { type: 'createTaskList' };
    }
    if (searchParams.get('create') === 'event') {
      return { 
        type: 'createEvent',
        data: { 
          defaultDate: searchParams.get('defaultDate'),
          defaultTime: searchParams.get('defaultTime')
        }
      };
    }
    if (searchParams.get('create') === 'person') {
      return { 
        type: 'createPerson',
        data: { category: searchParams.get('category') }
      };
    }
    if (searchParams.get('create') === 'contact') {
      return { type: 'createContact' };
    }
    if (searchParams.get('create') === 'document') {
      return { type: 'create_document' };
    }
    if (searchParams.get('create') === 'note') {
      return { type: 'create_note' };
    }
    if (searchParams.get('view') === 'person') {
      return { 
        type: 'viewPerson', 
        data: { id: searchParams.get('personId') } 
      };
    }
    if (searchParams.get('view') === 'task') {
      return { 
        type: 'viewTask', 
        data: { id: searchParams.get('taskId') } 
      };
    }
    if (searchParams.get('view') === 'event') {
      return { 
        type: 'viewEvent', 
        data: { id: searchParams.get('eventId') } 
      };
    }
    if (searchParams.get('view') === 'account') {
      return { type: 'account_settings' };
    }
    if (searchParams.get('view') === 'profile') {
      return { type: 'profile_edit' };
    }
    if (searchParams.get('view') === 'workspace') {
      return { type: 'workspace_settings' };
    }
    if (searchParams.get('view') === 'upgrade') {
      return { type: 'upgrade' };
    }
    if (searchParams.get('edit') || searchParams.get('editType')) {
      const editType = searchParams.get('editType');
      const id = searchParams.get('edit');
      if (editType === 'task') {
        return { type: 'editTask', data: { id } };
      }
      if (editType === 'tasklist') {
        return { type: 'editTaskList', data: { id } };
      }
      if (editType === 'event') {
        return { type: 'editEvent', data: { id } };
      }
      if (editType === 'person') {
        return { type: 'editPerson', data: { id } };
      }
      if (editType === 'contact') {
        return { type: 'editContact', data: { id } };
      }
      if (editType === 'document') {
        return { type: 'edit_document', data: { id } };
      }
    }
    return { type: null };
  }, [searchParams]);

  const currentModal = getModalFromParams();
  const isModalOpen = currentModal.type !== null;

  const openModal = useCallback((type: ModalType, data?: any) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Clear any existing modal params
    newSearchParams.delete('create');
    newSearchParams.delete('edit');
    newSearchParams.delete('editType');
    newSearchParams.delete('view');
    newSearchParams.delete('personId');
    newSearchParams.delete('taskId');
    newSearchParams.delete('eventId');
    
    if (type === 'viewPerson') {
      newSearchParams.set('view', 'person');
      newSearchParams.set('personId', data?.id || '');
    } else if (type === 'viewTask') {
      newSearchParams.set('view', 'task');
      newSearchParams.set('taskId', data?.id || '');
    } else if (type === 'viewEvent') {
      newSearchParams.set('view', 'event');
      newSearchParams.set('eventId', data?.id || '');
    } else if (type === 'create_note') {
      newSearchParams.set('create', 'note');
    } else if (type === 'create_document') {
      newSearchParams.set('create', 'document');
    } else if (type === 'account_settings') {
      newSearchParams.set('view', 'account');
    } else if (type === 'profile_edit') {
      newSearchParams.set('view', 'profile');
    } else if (type === 'workspace_settings') {
      newSearchParams.set('view', 'workspace');
    } else if (type === 'upgrade') {
      newSearchParams.set('view', 'upgrade');
    } else if (type.startsWith('create')) {
      let modalType = type.replace('create', '').toLowerCase();
      if (modalType === 'tasklist') modalType = 'tasklist';
      modalType = modalType.replace(/^_/, '');
      newSearchParams.set('create', modalType);
      if (data?.listId) {
        newSearchParams.set('listId', data.listId);
      }
      if (data?.category) {
        newSearchParams.set('category', data.category);
      }
      if (data?.defaultDate) {
        newSearchParams.set('defaultDate', data.defaultDate);
      }
      if (data?.defaultTime) {
        newSearchParams.set('defaultTime', data.defaultTime);
      }
    } else if (type === 'edit_document') {
      newSearchParams.set('edit', data?.id || '');
      newSearchParams.set('editType', 'document');
    } else if (type.startsWith('edit')) {
      let modalType = type.replace('edit', '').toLowerCase();
      if (modalType === 'tasklist') modalType = 'tasklist';
      modalType = modalType.replace(/^_/, '');
      newSearchParams.set('edit', data?.id || '');
      newSearchParams.set('editType', modalType);
    }
    
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  const closeModal = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('create');
    newSearchParams.delete('edit');
    newSearchParams.delete('editType');
    newSearchParams.delete('view');
    newSearchParams.delete('personId');
    newSearchParams.delete('taskId');
    newSearchParams.delete('eventId');
    newSearchParams.delete('listId');
    newSearchParams.delete('category');
    newSearchParams.delete('defaultDate');
    newSearchParams.delete('defaultTime');
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  return {
    currentModal,
    isModalOpen,
    openModal,
    closeModal,
  };
};